import React from 'react';
import { Card, Descriptions, Tag, Avatar, Typography, Space } from 'antd';
import { UserOutlined, CalendarOutlined, FlagOutlined } from '@ant-design/icons';
import { getEscalationLevelShortLabel, HAZARD_CLOSED_STATUSES } from '@iso/constants/hazardStatus';
import DeadlineCountdown from './DeadlineCountdown';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

const { Text, Title } = Typography;

/**
 * Hazard Assignment Card Component
 * Displays current assignment information for a hazard
 */
const HazardAssignmentCard = ({ hazard, workflowState, currentAssignment }) => {
  if (!hazard || !workflowState) {
    return null;
  }

  const assignee = currentAssignment?.assigned_to_user || workflowState.current_assignee;
  const deadline = hazard.deadline;
  const escalationLevel = hazard.current_escalation_level ?? 1;
  const isClosed = HAZARD_CLOSED_STATUSES.includes(hazard.status);
  // is_overdue is a stored flag reconciled by the escalation cron, so it can
  // lag a hazard that was just closed — don't paint a finished hazard red.
  const isOverdue = workflowState.is_overdue && !isClosed;

  return (
    <Card
      title={
        <Space>
          <UserOutlined />
          <span>Current Assignment</span>
        </Space>
      }
      bordered={false}
      style={{
        borderLeft: `4px solid ${isOverdue ? '#f5222d' : '#0B2A1B'}`,
        marginBottom: 16
      }}
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item
          label={
            <Space>
              <UserOutlined />
              <Text>Assigned To</Text>
            </Space>
          }
        >
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <Text strong>{assignee?.name || 'Unassigned'}</Text>
            {assignee?.email && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({assignee.email})
              </Text>
            )}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <FlagOutlined />
              <Text>Escalation Level</Text>
            </Space>
          }
        >
          <Tag color={
            escalationLevel === 0 ? 'default' :
            escalationLevel === 3 ? 'red' :
            escalationLevel === 2 ? 'orange' :
            '#0B2A1B'
          }>
            {getEscalationLevelShortLabel(escalationLevel)}
          </Tag>
          {escalationLevel === 0 && (
            <Tag color="default" style={{ marginLeft: 8 }}>
              CLOSED
            </Tag>
          )}
          {escalationLevel === 3 && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              URGENT
            </Tag>
          )}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <CalendarOutlined />
              <Text>Deadline</Text>
            </Space>
          }
        >
          <Space>
            {deadline && (
              <>
                <Text>{commonHelper.displayDateTimeLocal(deadline)}</Text>
                {!isClosed && <DeadlineCountdown deadline={deadline} />}
              </>
            )}
            {!deadline && <Text type="secondary">No deadline set</Text>}
          </Space>
        </Descriptions.Item>

        {currentAssignment?.assigned_at && (
          <Descriptions.Item
            label={<Text>Assigned At</Text>}
          >
            <Text type="secondary">
              {commonHelper.displayDateTimeLocal(currentAssignment.assigned_at)}
            </Text>
          </Descriptions.Item>
        )}

        {currentAssignment?.assigned_by_user && (
          <Descriptions.Item
            label={<Text>Assigned By</Text>}
          >
            <Text type="secondary">
              {currentAssignment.assigned_by_user.name}
            </Text>
          </Descriptions.Item>
        )}

        {workflowState.reopened_count > 0 && (
          <Descriptions.Item
            label={<Text>Reopen Count</Text>}
          >
            <Tag color="orange">
              Reopened {workflowState.reopened_count} time{workflowState.reopened_count !== 1 ? 's' : ''}
            </Tag>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default HazardAssignmentCard;
